import React, { useState, useEffect } from 'react';
import { getTeamsByOrganization, UpdateAgentur, deleteATeam, createTeam } from '../../api';

function ProjectManagementModal({ isOpen, onClose, user }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [editingProject, setEditingProject] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    isDefault: false
  });

  // Projekte laden
  const loadProjects = async () => {
    setLoading(true);
    setError('');
    try {
      // Ohne organizationId um alle Teams zu erhalten
      const response = await getTeamsByOrganization(user.id, null);
      if (response.success) {
        setProjects(response.teams || []);
      } else {
        setError(response.error || 'Fehler beim Laden der Projekte');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      loadProjects();
    }
  }, [isOpen, user.id]);

  // Erfolgsmeldung nach 3 Sekunden ausblenden
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Projekt bearbeiten
  const handleEditProject = (project) => {
    setEditingProject({
      id: project.id,
      name: project.name,
      description: project.description || '',
      isDefault: project.is_default || false,
      active: project.active !== false
    });
  };

  // Projekt speichern
  const handleSaveProject = async () => {
    if (!editingProject.name.trim()) {
      setError('Projektname ist erforderlich');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await UpdateAgentur(
        user.id,
        editingProject.id,
        editingProject.name,
        editingProject.description,
        editingProject.active
      );
      
      if (response.success) {
        setSuccessMessage('Projekt erfolgreich aktualisiert!');
        setEditingProject(null);
        loadProjects();
      } else {
        setError(response.error || 'Fehler beim Aktualisieren des Projekts');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Projekt löschen
  const handleDeleteProject = async (projectId) => {
    if (!window.confirm('Sind Sie sicher, dass Sie dieses Projekt löschen möchten?')) {
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await deleteATeam(user.id, projectId);
      if (response.success) {
        setSuccessMessage('Projekt erfolgreich gelöscht!');
        loadProjects();
      } else {
        setError(response.error || 'Fehler beim Löschen des Projekts');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // Neues Projekt erstellen
  const handleCreateProject = async () => {
    if (!newProject.name.trim()) {
      setError('Projektname ist erforderlich');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await createTeam(
        user.id,
        user.organization_id,
        newProject.name,
        newProject.description,
        newProject.isDefault
      );
      
      if (response.success) {
        setSuccessMessage('Projekt erfolgreich erstellt!');
        setNewProject({ name: '', description: '', isDefault: false });
        setShowCreateForm(false);
        loadProjects();
      } else {
        setError(response.error || 'Fehler beim Erstellen des Projekts');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-overlay project-management-modal" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Projekte verwalten</h2>
            <button className="close-button" onClick={onClose}>
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          </div>

          <div className="modal-body">
            {/* Erfolgs- und Fehlermeldungen */}
            {successMessage && (
              <div className="success-message">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22,4 12,14.01 9,11.01"></polyline>
                </svg>
                {successMessage}
              </div>
            )}
            
            {error && (
              <div className="error-message">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
                {error}
              </div>
            )}


            {user.role_type === 1 && (
              <div className="modal-actions">
                <button 
                  className="create-project-button"
                  onClick={() => setShowCreateForm(true)}
                  disabled={loading}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19"></line>
                    <line x1="5" y1="12" x2="19" y2="12"></line>
                  </svg>
                  Neues Projekt erstellen
                </button>
              </div>
            )}

            {/* Neues Projekt Formular */}
            {showCreateForm && (
              <div className="create-project-form">
                <h3>Neues Projekt erstellen</h3>
                <div className="form-group">
                  <label>Projektname *</label>
                  <input
                    type="text"
                    value={newProject.name}
                    onChange={(e) => setNewProject({...newProject, name: e.target.value})}
                    placeholder="Projektname eingeben"
                    disabled={loading}
                  />
                </div>
                <div className="form-group">
                  <label>Beschreibung</label>
                  <textarea
                    value={newProject.description}
                    onChange={(e) => setNewProject({...newProject, description: e.target.value})}
                    placeholder="Projektbeschreibung (optional)"
                    rows="3"
                    disabled={loading}
                  />
                </div>
                <div className="form-group checkbox-group">
                  <label>
                    <input
                      type="checkbox"
                      checked={newProject.isDefault}
                      onChange={(e) => setNewProject({...newProject, isDefault: e.target.checked})}
                      disabled={loading}
                    />
                    Standard-Projekt
                  </label>
                </div>
                <div className="form-actions">
                  <button 
                    className="save-button"
                    onClick={handleCreateProject}
                    disabled={loading}
                  >
                    {loading ? 'Erstelle...' : 'Projekt erstellen'}
                  </button>
                  <button 
                    className="cancel-button"
                    onClick={() => {
                      setShowCreateForm(false);
                      setNewProject({ name: '', description: '', isDefault: false });
                    }}
                    disabled={loading}
                  >
                    Abbrechen
                  </button>
                </div>
              </div>
            )}

            {/* Projekte Liste */}
            <div className="projects-list">
              <h3>Alle Projekte ({projects.length})</h3>
              
              {loading && !showCreateForm ? (
                <div className="loading-message">
                  <div className="loading-spinner"></div>
                  <span>Lade Projekte...</span>
                </div>
              ) : projects.length === 0 ? (
                <div className="empty-state">
                  <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                  </svg>
                  <p>Keine Projekte gefunden</p>
                  <button 
                    className="create-first-project-button"
                    onClick={() => setShowCreateForm(true)}
                  >
                    Erstes Projekt erstellen
                  </button>
                </div>
              ) : (
                <div className="projects-grid">
                  {projects.map((project) => (
                    <div key={project.id} className="project-card">
                      {editingProject && editingProject.id === project.id ? (
                        // Bearbeitungsmodus
                        <div className="edit-project-form">
                          <div className="form-group">
                            <label>Projektname *</label>
                            <input
                              type="text"
                              value={editingProject.name}
                              onChange={(e) => setEditingProject({...editingProject, name: e.target.value})}
                              disabled={loading}
                            />
                          </div>
                          <div className="form-group">
                            <label>Beschreibung</label>
                            <textarea
                              value={editingProject.description}
                              onChange={(e) => setEditingProject({...editingProject, description: e.target.value})}
                              rows="2"
                              disabled={loading}
                            />
                          </div>
                          <div className="form-group checkbox-group">
                            <label>
                              <input
                                type="checkbox"
                                checked={editingProject.active}
                                onChange={(e) => setEditingProject({...editingProject, active: e.target.checked})}
                                disabled={loading}
                              />
                              Aktiv
                            </label>
                          </div>
                          <div className="project-actions">
                            <button 
                              className="save-button small"
                              onClick={handleSaveProject}
                              disabled={loading}
                            >
                              {loading ? 'Speichere...' : 'Speichern'}
                            </button>
                            <button 
                              className="cancel-button small"
                              onClick={() => setEditingProject(null)}
                              disabled={loading}
                            >
                              Abbrechen
                            </button>
                          </div>
                        </div>
                      ) : (
                        // Anzeigemodus
                        <>
                            <div className="project-header">
                              <h4>{project.name}</h4>
                              <div className="project-badges">
                                {!project.isActive ? (
                                  <span className="badge inactive">Inaktiv</span>
                                ) : (
                                  <span className="badge active">Aktiv</span>
                                )}
                              </div>
                            </div>
                          
                          {project.description && (
                            <p className="project-description">{project.description}</p>
                          )}
                          
                          <div className="project-info">
                            <span className="member-count">
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                <circle cx="9" cy="7" r="4"></circle>
                                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                              </svg>
                              {project.memberCount || 0} Mitglieder
                            </span>
                          </div>
                          
                          <div className="project-actions">
                            <button 
                              className="edit-button"
                              onClick={() => handleEditProject(project)}
                              disabled={loading}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
                              </svg>
                              Bearbeiten
                            </button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>


    </>
  );
}

export default ProjectManagementModal; 